/**
 * @fileoverview Servicio para gestión del LocalStorage de la sociedad
 * @description Maneja la persistencia de datos de sociedad en el navegador
 */

import { Sociedad } from '../types';

const SOCIEDAD_STORAGE_KEY = 'cg-seguros-sociedad';
const TIPO_FORMACION_KEY = 'cg-seguros-tipo-formacion';

/**
 * Servicio para gestión del LocalStorage de la sociedad
 */
export const sociedadStorage = {
  /**
   * Guarda los datos de la sociedad en LocalStorage
   * @param sociedad - Datos de la sociedad a guardar
   */
  saveSociedad(sociedad: Sociedad): void {
    try {
      localStorage.setItem(SOCIEDAD_STORAGE_KEY, JSON.stringify(sociedad));
    } catch (error) {
      return undefined;
    }
  },

  /**
   * Obtiene los datos de la sociedad desde LocalStorage
   * @returns Datos de la sociedad o null si no existen
   */
  getSociedad(): Sociedad | null {
    try {
      const stored = localStorage.getItem(SOCIEDAD_STORAGE_KEY);
      if (stored) {
        const sociedad = JSON.parse(stored);
        return sociedad;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Obtiene el ID de la sociedad desde LocalStorage
   * @returns ID de la sociedad o null si no existe
   */
  getSociedadId(): number | null {
    const sociedad = this.getSociedad();
    return sociedad?.idSociedad || null;
  },

  /**
   * Limpia los datos de la sociedad del LocalStorage
   */
  clearSociedad(): void {
    try {
      localStorage.getItem(SOCIEDAD_STORAGE_KEY);
      localStorage.removeItem(SOCIEDAD_STORAGE_KEY);
      localStorage.getItem(SOCIEDAD_STORAGE_KEY);
    } catch (error) {
      return undefined;
    }
  },

  /**
   * Verifica si existe una sociedad guardada
   * @returns true si existe una sociedad guardada
   */
  hasSociedad(): boolean {
    return this.getSociedad() !== null;
  },

  /**
   * Guarda el tipo de formación seleccionado en LocalStorage
   * @param tipo - Tipo de formación ('accionFormativa' | 'curso')
   */
  saveTipoFormacion(tipo: 'accionFormativa' | 'curso'): void {
    try {
      localStorage.setItem(TIPO_FORMACION_KEY, tipo);
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  },

  /**
   * Obtiene el tipo de formación guardado desde LocalStorage
   * @returns Tipo de formación o null si no existe
   */
  getTipoFormacion(): 'accionFormativa' | 'curso' | null {
    try {
      const stored = localStorage.getItem(TIPO_FORMACION_KEY);
      if (stored && (stored === 'accionFormativa' || stored === 'curso')) {
        return stored as 'accionFormativa' | 'curso';
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Limpia el tipo de formación del LocalStorage
   */
  clearTipoFormacion(): void {
    try {
      localStorage.removeItem(TIPO_FORMACION_KEY);
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }
};
